import { type QueryKey, useQueryClient } from "@tanstack/react-query";

import type { ChatMessage, ChatMessageWithUIState } from "@virtual-brain-lib/types";

export type ChatMessagesPageData = {
	pages: ChatMessageWithUIState[][];
	pageParams: number[];
};

export function normalizeProcessingMessage(message: ChatMessage): ChatMessageWithUIState {
	if (message.role === "assistant" && message.status === "processing") {
		return {
			...message,
			isThinking: true,
			answeringStep: { step: "analyzing", status: "pending" },
		};
	}

	return message;
}

interface AddCachedMessagesParams {
	queryKey: QueryKey;
	messages: ChatMessage[];
}

interface RemoveCachedMessageParams {
	queryKey: QueryKey;
	messageId: string;
}

export interface GetCachedMessageParams<TSelected = ChatMessageWithUIState | undefined> {
	queryKey: QueryKey;
	messageId: string;
	select?: (message: ChatMessageWithUIState | undefined) => TSelected;
}

export interface GetCachedMessageByArtifactIdParams {
	queryKey: QueryKey;
	artifactId: string;
}

interface ReplaceCachedOptimisticMessageParams {
	queryKey: QueryKey;
	optimisticId: string;
	newMessages: ChatMessage[];
}

export interface UpdateCachedMessageParams {
	queryKey: QueryKey;
	messageId: string;
	updates: Partial<ChatMessageWithUIState>;
}

export function useChatMessagesCache() {
	const queryClient = useQueryClient();

	const getCachedMessage = <TSelected = ChatMessageWithUIState | undefined>({
		queryKey,
		messageId,
		select,
	}: GetCachedMessageParams<TSelected>): TSelected => {
		const data = queryClient.getQueryData<ChatMessagesPageData>(queryKey);

		let message: ChatMessageWithUIState | undefined;

		if (data?.pages) {
			for (const page of data.pages) {
				message = page.find((msg) => msg.id === messageId);

				if (message) break;
			}
		}

		if (select) {
			return select(message);
		}

		return message as TSelected;
	};

	const getCachedMessageByArtifactId = ({
		queryKey,
		artifactId,
	}: GetCachedMessageByArtifactIdParams): ChatMessageWithUIState | undefined => {
		const data = queryClient.getQueryData<ChatMessagesPageData>(queryKey);

		if (!data?.pages) {
			return undefined;
		}

		for (const page of data.pages) {
			const message = page.find((msg) => msg.artifacts?.some((a) => a.id === artifactId));

			if (message) {
				return message;
			}
		}

		return undefined;
	};

	const addCachedMessages = ({ queryKey, messages }: AddCachedMessagesParams) => {
		const normalized = messages.map(normalizeProcessingMessage);

		queryClient.setQueryData<ChatMessagesPageData>(queryKey, (oldData) => {
			if (!oldData) {
				return {
					pages: [normalized],
					pageParams: [0],
				};
			}

			const updatedPages = oldData.pages.map((page, index) =>
				index === 0 ? [...page, ...normalized] : page,
			);

			return { ...oldData, pages: updatedPages };
		});
	};

	const removeCachedMessage = ({ queryKey, messageId }: RemoveCachedMessageParams) => {
		queryClient.setQueryData<ChatMessagesPageData>(queryKey, (oldData) => {
			if (!oldData) {
				return oldData;
			}

			const updatedPages = oldData.pages.map((page) =>
				page.filter((msg) => msg.id !== messageId),
			);

			return { ...oldData, pages: updatedPages };
		});
	};

	const replaceCachedOptimisticMessage = ({
		queryKey,
		optimisticId,
		newMessages,
	}: ReplaceCachedOptimisticMessageParams) => {
		const normalized = newMessages.map(normalizeProcessingMessage);

		queryClient.setQueryData<ChatMessagesPageData>(queryKey, (oldData) => {
			if (!oldData) {
				return {
					pages: [normalized],
					pageParams: [0],
				};
			}

			const updatedPages = oldData.pages.map((page, index) => {
				const filtered = page.filter((msg) => msg.id !== optimisticId);

				return index === 0 ? [...filtered, ...normalized] : filtered;
			});

			return { ...oldData, pages: updatedPages };
		});
	};

	const updateCachedMessage = ({ queryKey, messageId, updates }: UpdateCachedMessageParams) => {
		queryClient.setQueryData<ChatMessagesPageData>(queryKey, (oldData) => {
			if (!oldData) {
				return oldData;
			}

			const updatedPages = oldData.pages.map((page) => {
				const idx = page.findIndex((msg) => msg.id === messageId);

				if (idx === -1) return page;

				const updatedPage = [...page];

				updatedPage[idx] = { ...page[idx], ...updates };

				return updatedPage;
			});

			return { ...oldData, pages: updatedPages };
		});
	};

	return {
		getCachedMessage,
		getCachedMessageByArtifactId,
		addCachedMessages,
		removeCachedMessage,
		replaceCachedOptimisticMessage,
		updateCachedMessage,
	};
}
